import { type InvoiceFormOptions } from '@utazzo/shared';
import { UzButton, UzCard, UzDatePicker, UzInput, UzSelect, type UzSelectOption } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo } from 'react';

import { emptyPassengerRow, type PassengerRow, type PaxType } from './invoice-form.helpers';

import { useCountryOptions } from '@/features/airlines/use-airlines';

interface PassengerGridProps {
  options: InvoiceFormOptions | undefined;
  rows: PassengerRow[];
  onChange: (rows: PassengerRow[]) => void;
  isInternational: boolean;
  showErrors: boolean;
  invalid: boolean;
}

/**
 * Section 6 - Passengers (always shown, >=1 required). Passport / expiry /
 * place-of-issue / nationality columns appear only for international product
 * types.
 */
export const PassengerGrid = ({
  options,
  rows,
  onChange,
  isInternational,
  showErrors,
  invalid,
}: PassengerGridProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<PassengerRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const titleOptions = useMemo<ReadonlyArray<UzSelectOption<string>>>(
    () => (options?.titles ?? []).map((t) => ({ value: t, label: t })),
    [options?.titles],
  );
  const paxOptions = useMemo<ReadonlyArray<UzSelectOption<PaxType>>>(
    () => (options?.paxTypes ?? []).map((p) => ({ value: p.code as PaxType, label: p.name })),
    [options?.paxTypes],
  );

  const countryQuery = useCountryOptions();
  const countryOptions = useMemo<ReadonlyArray<UzSelectOption>>(
    () => (countryQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [countryQuery.data],
  );

  return (
    <UzCard
      title="Passengers"
      description="Names printed on the invoice. At least one is required."
      actions={
        <UzButton
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...rows, emptyPassengerRow()])}
        >
          Add passenger
        </UzButton>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {showErrors && invalid ? (
            <span className="text-red-600">Add at least one passenger.</span>
          ) : (
            'No passengers added.'
          )}
        </p>
      ) : (
        <div className="space-y-5">
          {rows.map((row, idx) => (
            <div key={idx} className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-500">Passenger {idx + 1}</span>
                <UzButton
                  variant="ghost"
                  size="sm"
                  aria-label="Remove passenger"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-4 w-4" />
                </UzButton>
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                <UzSelect
                  label="Title"
                  placeholder="Title"
                  options={titleOptions}
                  value={row.title || undefined}
                  onValueChange={(v) => update(idx, { title: v })}
                />
                <UzInput
                  label="First name"
                  value={row.firstName}
                  onChange={(e) => update(idx, { firstName: e.target.value })}
                />
                <UzInput
                  label="Middle name"
                  value={row.middleName}
                  onChange={(e) => update(idx, { middleName: e.target.value })}
                />
                <UzInput
                  label="Last name"
                  value={row.lastName}
                  onChange={(e) => update(idx, { lastName: e.target.value })}
                />
                <UzSelect<PaxType>
                  label="Pax type"
                  options={paxOptions}
                  value={row.paxType}
                  onValueChange={(v) => update(idx, { paxType: v })}
                />
                <UzDatePicker
                  label="Date of birth"
                  value={row.dob}
                  onValueChange={(v) => update(idx, { dob: v })}
                  maxDate={new Date()}
                />
                <UzInput
                  label="PAN"
                  value={row.panNo}
                  onChange={(e) => update(idx, { panNo: e.target.value })}
                />
              </div>
              {isInternational && (
                <div className="mt-3 grid gap-3 border-t border-zinc-100 pt-3 md:grid-cols-4">
                  <UzInput
                    label="Passport no."
                    value={row.passportNumber}
                    onChange={(e) => update(idx, { passportNumber: e.target.value })}
                  />
                  <UzDatePicker
                    label="Passport expiry"
                    value={row.expiryDate}
                    onValueChange={(v) => update(idx, { expiryDate: v })}
                  />
                  <UzInput
                    label="Place of issue"
                    value={row.placeOfIssue}
                    onChange={(e) => update(idx, { placeOfIssue: e.target.value })}
                  />
                  <UzSelect
                    label="Nationality"
                    placeholder={countryQuery.isLoading ? 'Loading...' : 'Select country'}
                    options={countryOptions}
                    value={row.nationalityCountryId || undefined}
                    onValueChange={(v) => update(idx, { nationalityCountryId: v })}
                    disabled={countryQuery.isLoading}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </UzCard>
  );
};
